import React, { useState, useEffect } from 'react';
import { useAuthHeader } from 'react-auth-kit';
import axios from 'axios';
import { Card, Row, Col, Table, Badge, ButtonGroup, Button, Spinner } from 'react-bootstrap';
import * as Icon from 'react-feather';

function FinancialDashboard() {
    const authHeader = useAuthHeader();
    const apiUrl = process.env.REACT_APP_URL;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [months, setMonths] = useState(12);

    const loadData = async (m) => {
        setLoading(true);
        try {
            const res = await axios.get(`${apiUrl}/finance/dashboard?months=${m}`, {
                headers: { Authorization: authHeader() }
            });
            setData(res.data.msg);
        } catch (err) {
            console.error('Error loading dashboard:', err);
        }
        setLoading(false);
    };

    useEffect(() => {
        loadData(months);
    }, [months]);

    const fmt = (n) => {
        if (n == null) return '$0.00';
        return '$' + parseFloat(n).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const fmtMonth = (m) => {
        const [y, mo] = m.split('-');
        const names = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        return `${names[parseInt(mo) - 1]} ${y}`;
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" />
                <p className="mt-2">Cargando dashboard...</p>
            </div>
        );
    }

    if (!data) {
        return <div className="text-center text-muted py-5">No hay datos disponibles</div>;
    }

    const { summary, monthly, top_clients } = data;

    // Calculate the max value for the bar chart scaling
    const maxBarValue = Math.max(
        ...monthly.map(m => Math.max(m.revenue, m.expenses)),
        1
    );

    return (
        <div>
            {/* Period Filter */}
            <div className="mb-4 d-flex justify-content-end">
                <ButtonGroup>
                    {[
                        { label: '3 Meses', val: 3 },
                        { label: '6 Meses', val: 6 },
                        { label: '1 Ano', val: 12 },
                        { label: '2 Anos', val: 24 },
                        { label: 'Todo', val: 60 },
                    ].map(opt => (
                        <Button
                            key={opt.val}
                            size="sm"
                            variant={months === opt.val ? 'primary' : 'outline-primary'}
                            onClick={() => setMonths(opt.val)}
                        >
                            {opt.label}
                        </Button>
                    ))}
                </ButtonGroup>
            </div>

            {/* KPI Cards */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="text-center">
                            <div className="text-muted small mb-1">Ingresos</div>
                            <div className="fs-4 fw-bold text-success">{fmt(summary.total_revenue)}</div>
                            <Badge bg="success" className="mt-1">{summary.total_trips} viajes</Badge>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="text-center">
                            <div className="text-muted small mb-1">Gastos</div>
                            <div className="fs-4 fw-bold text-danger">{fmt(summary.total_expenses)}</div>
                            <div className="small text-muted mt-1">
                                Diesel: {fmt(summary.total_diesel)} | Tag: {fmt(summary.total_tags)}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="text-center">
                            <div className="text-muted small mb-1">Utilidad</div>
                            <div className={`fs-4 fw-bold ${summary.profit >= 0 ? 'text-primary' : 'text-danger'}`}>
                                {fmt(summary.profit)}
                            </div>
                            <div className="small text-muted mt-1">
                                Margen: {summary.total_revenue > 0 ? ((summary.profit / summary.total_revenue) * 100).toFixed(1) : 0}%
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body className="text-center">
                            <div className="text-muted small mb-1">Diesel</div>
                            <div className="fs-4 fw-bold text-warning">{parseFloat(summary.total_liters || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })} L</div>
                            <div className="small text-muted mt-1">
                                {summary.total_liters > 0
                                    ? `$${(summary.total_diesel / summary.total_liters).toFixed(2)}/L`
                                    : '-'
                                }
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Secondary KPIs */}
            <Row className="mb-4">
                <Col md={2}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="text-center py-2">
                            <div className="text-muted small">Viajes Pagados</div>
                            <div className="fs-5 fw-bold text-success">{summary.paid_trips}</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="text-center py-2">
                            <div className="text-muted small">Por Cobrar</div>
                            <div className="fs-5 fw-bold text-warning">{summary.unpaid_trips}</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={2}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="text-center py-2">
                            <div className="text-muted small">Entregados</div>
                            <div className="fs-5 fw-bold text-info">{summary.delivered_trips}</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="text-center py-2">
                            <div className="text-muted small">Maniobras</div>
                            <div className="fs-5 fw-bold">{fmt(summary.total_maneuvers)}</div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="text-center py-2">
                            <div className="text-muted small">Estadias</div>
                            <div className="fs-5 fw-bold">{fmt(summary.total_stays)}</div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Monthly Table + Top Clients */}
            <Row>
                <Col md={8}>
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-white">
                            <h6 className="mb-0">Resumen Mensual</h6>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table responsive className="mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th>Mes</th>
                                        <th>Viajes</th>
                                        <th>Ingresos</th>
                                        <th>Diesel</th>
                                        <th>Tags</th>
                                        <th>Gastos</th>
                                        <th>Utilidad</th>
                                        <th style={{ width: '20%' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {monthly.slice().reverse().map((m) => (
                                        <tr key={m.month}>
                                            <td className="fw-semibold">{fmtMonth(m.month)}</td>
                                            <td><Badge bg="info">{m.trips}</Badge></td>
                                            <td className="text-success">{fmt(m.revenue)}</td>
                                            <td>{fmt(m.diesel)}</td>
                                            <td>{fmt(m.tags)}</td>
                                            <td className="text-danger">{fmt(m.expenses)}</td>
                                            <td className={m.profit >= 0 ? 'text-primary fw-bold' : 'text-danger fw-bold'}>
                                                {fmt(m.profit)}
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-1" style={{ height: '20px' }}>
                                                    <div
                                                        style={{
                                                            width: `${Math.max((m.revenue / maxBarValue) * 100, 2)}%`,
                                                            height: '8px',
                                                            backgroundColor: '#198754',
                                                            borderRadius: '4px',
                                                        }}
                                                    />
                                                    <div
                                                        style={{
                                                            width: `${Math.max((m.expenses / maxBarValue) * 100, 2)}%`,
                                                            height: '8px',
                                                            backgroundColor: '#dc3545',
                                                            borderRadius: '4px',
                                                        }}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                {monthly.length > 0 && (
                                    <tfoot className="bg-light fw-bold">
                                        <tr>
                                            <td>Total</td>
                                            <td><Badge bg="dark">{monthly.reduce((s, m) => s + m.trips, 0)}</Badge></td>
                                            <td className="text-success">{fmt(monthly.reduce((s, m) => s + m.revenue, 0))}</td>
                                            <td>{fmt(monthly.reduce((s, m) => s + m.diesel, 0))}</td>
                                            <td>{fmt(monthly.reduce((s, m) => s + m.tags, 0))}</td>
                                            <td className="text-danger">{fmt(monthly.reduce((s, m) => s + m.expenses, 0))}</td>
                                            <td className={summary.profit >= 0 ? 'text-primary' : 'text-danger'}>
                                                {fmt(summary.profit)}
                                            </td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                )}
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-white">
                            <h6 className="mb-0">Top Clientes por Ingreso</h6>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table responsive className="mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th>#</th>
                                        <th>Cliente</th>
                                        <th>Ingresos</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {top_clients.map((c, i) => (
                                        <tr key={c.name}>
                                            <td>{i + 1}</td>
                                            <td>{c.name}</td>
                                            <td className="text-success fw-semibold">{fmt(c.revenue)}</td>
                                        </tr>
                                    ))}
                                    {top_clients.length === 0 && (
                                        <tr>
                                            <td colSpan="3" className="text-center text-muted">Sin datos</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>

                    {/* Expense Breakdown */}
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white">
                            <h6 className="mb-0">Desglose de Gastos</h6>
                        </Card.Header>
                        <Card.Body>
                            {[
                                { label: 'Diesel', value: summary.total_diesel, color: '#ffc107', icon: <Icon.Zap size={16} /> },
                                { label: 'Tags', value: summary.total_tags, color: '#0dcaf0', icon: <Icon.Tag size={16} /> },
                                { label: 'Maniobras', value: summary.total_maneuvers, color: '#6f42c1', icon: <Icon.Tool size={16} /> },
                                { label: 'Estadias', value: summary.total_stays, color: '#fd7e14', icon: <Icon.Clock size={16} /> },
                            ].map(item => {
                                const totalAll = summary.total_diesel + summary.total_tags + summary.total_maneuvers + summary.total_stays;
                                const pct = totalAll > 0 ? ((item.value / totalAll) * 100).toFixed(1) : 0;
                                return (
                                    <div key={item.label} className="mb-3">
                                        <div className="d-flex justify-content-between mb-1">
                                            <span className="small">{item.icon} {item.label}</span>
                                            <span className="small fw-semibold">{fmt(item.value)} ({pct}%)</span>
                                        </div>
                                        <div className="progress" style={{ height: '6px' }}>
                                            <div
                                                className="progress-bar"
                                                style={{
                                                    width: `${pct}%`,
                                                    backgroundColor: item.color,
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}

export default FinancialDashboard;
